// src/OrthoCesiumLayer.jsx
import { useEffect, useRef } from "react";

// --- Helpers ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

const mercatorToLatLng = (x, y) => {
  const rMajor = 6378137;
  const shift = Math.PI * rMajor;
  const lon = (x / shift) * 180.0;
  let lat = (y / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lat, lon];
};

export default function OrthoCesiumLayer({ viewer, latestSurvey, apiBase }) {
  // We store the active layer objects here so we can target them for removal
  const activeLayersRef = useRef([]); 

  // --- 1. Build URL ---
  const buildTileUrl = (cand, apiBase) => {
    // We strictly use the 'tile_url_root' provided by the backend to ensure
    // we use the correct folder slugs (e.g. "site-a" not "Site A").
    if (cand.tile_url_root) {
      try {
        const serverUrl = new URL(cand.tile_url_root);
        const serverPath = serverUrl.pathname; 
        const cleanBase = apiBase.replace(/\/+$/, "");
        
        // Construct: apiBase + path from server + tile pattern
        return `${cleanBase}${serverPath}{z}/{x}/{y}.png`;
      } catch (e) { 
        console.warn("Invalid tile_url_root:", cand.tile_url_root);
      }
    }
    return null;
  };

  // --- 2. Fetch Metadata & Create Cesium Rectangle ---
  const fetchLayerMetadata = async (tileUrlTemplate) => {
    if (!tileUrlTemplate) return null;
    try {
      // Replace tile pattern with metadata file
      const xmlUrl = tileUrlTemplate.replace(/\{z\}\/\{x\}\/\{y\}\.png$/, "tilemapresource.xml");
      
      const resp = await fetch(xmlUrl);
      if (!resp.ok) return null;
      
      const text = await resp.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const bbox = xmlDoc.querySelector("BoundingBox");

      if (bbox) {
        const minx = parseFloat(bbox.getAttribute("minx"));
        const miny = parseFloat(bbox.getAttribute("miny"));
        const maxx = parseFloat(bbox.getAttribute("maxx"));
        const maxy = parseFloat(bbox.getAttribute("maxy"));

        let west, south, east, north;

        // Check if values are Lat/Lon (<= 180) or Mercator (Huge numbers)
        if (Math.abs(minx) <= 180 && Math.abs(miny) <= 90) {
            west = minx; south = miny; east = maxx; north = maxy;
        } else {
            // Convert Mercator to Lat/Lon
            const [lat1, lon1] = mercatorToLatLng(minx, miny);
            const [lat2, lon2] = mercatorToLatLng(maxx, maxy);
            west = lon1; south = lat1; east = lon2; north = lat2;
        }
        
        // Return a Cesium Rectangle object (expects degrees)
        const Cesium = window.Cesium;
        return Cesium.Rectangle.fromDegrees(west, south, east, north);
      }
    } catch (e) {
      console.warn("[OrthoCesium] XML parse error:", e);
    }
    return null;
  };

  // --- Helper: Strict Cleanup ---
  const removeAllLayers = () => {
    if (!viewer || !activeLayersRef.current) return;
    
    activeLayersRef.current.forEach(item => {
      if (item.layer && !viewer.imageryLayers.isDestroyed()) {
        if (viewer.imageryLayers.contains(item.layer)) {
          viewer.imageryLayers.remove(item.layer, true); // true = destroy geometry
        }
      }
    });
    activeLayersRef.current = [];
  };

  // --- 3. Main Discovery Logic ---
  useEffect(() => {
    const Cesium = window.Cesium;
    if (!viewer || !Cesium) return;

    let isMounted = true;

    const discoverAndRender = async () => {
      // Cleanup existing layers
      removeAllLayers();

      if (!latestSurvey?.location_name || !latestSurvey?.survey_time) {
        window.dispatchEvent(new CustomEvent("layers-discovered", { 
            detail: { layer_names: [], candidates: [] } 
        }));
        return;
      }

      try {
        const params = new URLSearchParams({
          location_name: String(latestSurvey.location_name),
          survey_time: toDateKey(latestSurvey.survey_time),
        });
        
        // Use the backend discovery endpoint
        const url = `${apiBase}/layers/tiles-discovery?${params.toString()}`;
        console.log(`[OrthoCesium] Discovering: ${url}`);
        
        const r = await fetch(url);
        if (!isMounted) return; 
        if (!r.ok) return;
        
        const json = await r.json();
        const foundCandidates = Array.isArray(json.found) ? json.found : [];

        if (!isMounted) return;

        let boundsToZoom = null;
        const layerNamesList = [];

        // Process Layers
        for (const [index, cand] of foundCandidates.entries()) {
          // Use the URL builder
          const tileUrl = buildTileUrl(cand, apiBase);
          
          if (!tileUrl) {
              console.warn("Skipping layer due to URL build fail:", cand.layer_slug);
              continue;
          }

          const layerName = cand.layer_slug || cand.layer_name || `Ortho ${index + 1}`;
          
          // FIX: Use index in ID to match Leaflet/Sidebar logic
          const uniqueId = `auto-ortho-${layerName}-${index}`;

          // Create Provider
          // Note: We replace {y} with {reverseY} for TMS support in Cesium
          const provider = new Cesium.UrlTemplateImageryProvider({
            url: tileUrl.replace("{y}", "{reverseY}"), 
            tilingScheme: new Cesium.WebMercatorTilingScheme(),
            maximumLevel: 24,
            credit: new Cesium.Credit(layerName),
          });

          // Add to Viewer
          const layer = viewer.imageryLayers.addImageryProvider(provider);
          layer.alpha = 1.0; 
          layer.show = true;

          // Track layer
          activeLayersRef.current.push({ id: uniqueId, layer: layer, name: layerName });
          layerNamesList.push(layerName);

          // Calculate Bounds (only need to do this once for the first valid layer)
          if (!boundsToZoom) {
             boundsToZoom = await fetchLayerMetadata(tileUrl);
          }
        }

        // Dispatch & Zoom
        if (isMounted) {
          window.dispatchEvent(new CustomEvent("layers-discovered", {
             detail: { 
               location: latestSurvey.location_name, 
               survey_time: latestSurvey.survey_time, 
               layer_names: layerNamesList, 
               candidates: foundCandidates 
             }
          }));

          if (boundsToZoom) {
            console.log("[OrthoCesium] Flying to bounds:", boundsToZoom);
            viewer.camera.flyTo({ 
                destination: boundsToZoom, 
                duration: 1.5 
            });
          }
        }

      } catch (err) {
        console.error("[OrthoCesium] Error:", err);
      }
    };

    discoverAndRender();

    return () => {
      isMounted = false;
      removeAllLayers();
    };
  }, [viewer, latestSurvey, apiBase]); 

  // --- 4. Handle Events (Opacity & Toggle) ---
  useEffect(() => {
    const handleOpacity = (ev) => {
      const { id, opacity } = ev?.detail || {};
      
      // Look for ID first, fallback to name if needed (but ID is preferred now)
      const target = activeLayersRef.current.find(item => item.id === id || item.name === id);
      
      if (target && target.layer && !target.layer.isDestroyed()) {
        target.layer.alpha = Number(opacity);
        viewer.scene.requestRender(); 
      }
    };

    const handleToggle = (ev) => {
      const { id, visible } = ev?.detail || {};
      const target = activeLayersRef.current.find(item => item.id === id || item.name === id);
      
      if (target && target.layer && !target.layer.isDestroyed()) {
        target.layer.show = !!visible;
        // Optional: Raise to top if made visible to ensure visibility over others? 
        // Typically not needed if user just wants to toggle on/off in place.
        viewer.scene.requestRender();
      }
    };

    window.addEventListener("map-set-opacity", handleOpacity);
    window.addEventListener("map-toggle-layer", handleToggle);

    return () => {
      window.removeEventListener("map-set-opacity", handleOpacity);
      window.removeEventListener("map-toggle-layer", handleToggle);
    };
  }, [viewer]);

  return null;
}
